import { useState, type ReactNode } from 'react'
import { api } from '../lib/api'
import type { Unit } from '../lib/types'
import type { StaffKey } from '../lib/staffText'
import { Button } from '@heroui/react'
import { useStaffText } from '../ui/StaffText'
import { Glyph } from './Editor'
import { StudioSelect } from './heroParts'
import { Group, SizeField, Seg, Swatches, Toggle } from './Inspector'
import { PicturePicker } from './PicturePicker'
import { paperOnly, themeOffered } from './controls'
import { SCREEN_CHANGES, screenChangeOf, withScreenChange } from './counterMotion'
import { STANDARD_FONT, fontOptions, withCanvasFont } from './fonts'
import { GRADIENTS, gradientPaint, lookOffered, orientation, type Gradient } from './look'
import type { BackgroundFill, CanvasTheme, LayoutCanvas, LayoutDocument, LayoutKind, ScreenChange } from './types'
import '../styles/studio.css'

const KIND_NAME: Record<LayoutKind, StaffKey> = {
  display: 'designs.page.kind.display',
  kiosk: 'designs.page.kind.kiosk',
  ticket: 'designs.page.kind.ticket',
  counter: 'designs.page.kind.counter',
}

const KIND_GLYPH: Record<LayoutKind, 'display' | 'kiosk' | 'ticketNumber'> = {
  display: 'display',
  kiosk: 'kiosk',
  ticket: 'ticketNumber',
  counter: 'display',
}

const ORIENTATION: Record<'portrait' | 'landscape', StaffKey> = {
  portrait: 'designs.look.portrait',
  landscape: 'designs.look.landscape',
}

const GRADIENT_NAME: Record<Gradient, StaffKey> = {
  dawn: 'designs.look.g.dawn',
  mist: 'designs.look.g.mist',
  dusk: 'designs.look.g.dusk',
}

/** At most this many machines are listed; the rest are a count. */
const LIVE_ROWS = 6

/**
 * The right panel with nothing selected: the design's own look and where it is
 * live. What stops a publish is in the header's "to fix" list.
 */
export function DesignPanel({
  document: doc,
  layoutId,
  onChangeCanvas,
  onResize,
  machines,
  allBranches,
  usageKnown = true,
  children,
}: {
  document: LayoutDocument
  /** The open design, whose version a machine reports showing. */
  layoutId: string
  onChangeCanvas: (canvas: LayoutCanvas) => void
  onResize: (width: number, height: number) => void
  machines: readonly Unit[]
  /** The organisation's default for its kind. */
  allBranches: boolean
  usageKnown?: boolean
  /** A kiosk page's own settings. */
  children?: ReactNode
}) {
  const { say } = useStaffText()
  const c = doc.canvas
  const offered = lookOffered(doc.kind)
  const theme: CanvasTheme = c.theme ?? 'dark'
  const fill: BackgroundFill = c.backgroundFill ?? 'colour'
  // A background picture is being chosen.
  const [picking, setPicking] = useState(false)
  const set = (patch: Partial<LayoutCanvas>) => onChangeCanvas({ ...c, ...patch })

  const size =
    doc.kind === 'ticket'
      ? say('designs.look.paperLine', { w: c.width, h: c.height })
      : say('designs.look.sizeLine', { orientation: say(ORIENTATION[orientation(c)]), w: c.width, h: c.height })

  return (
    <div className="studio-look">
      <div className="studio-look__head">
        <span className="studio-look__icon">
          <Glyph of={KIND_GLYPH[doc.kind]} size={18} />
        </span>
        <div>
          <h2>{say(KIND_NAME[doc.kind])}</h2>
          <p>{size}</p>
        </div>
      </div>

      <div className="studio-look__sec">
        {doc.kind !== 'ticket' && <SizeField key={`${c.width}x${c.height}`} document={doc} onResize={onResize} />}

        {offered.theme && themeOffered(doc.kind, c.theme) && (
          <Group label={say('designs.insp.theme')}>
            <Seg
              label={say('designs.insp.theme')}
              value={theme}
              options={[
                ['studio', say('designs.insp.studio')],
                ['light', say('designs.insp.light')],
                ['dark', say('designs.insp.dark')],
              ]}
              onChange={(t) => set({ theme: t as CanvasTheme })}
            />
          </Group>
        )}
        {/* A ticket left dark keeps the switch until it is light (controls.themeOffered). */}
        {!offered.theme && themeOffered(doc.kind, c.theme) && (
          <Group label={say('designs.insp.theme')}>
            <Seg
              label={say('designs.insp.theme')}
              value={theme}
              options={[
                ['light', say('designs.insp.light')],
                ['dark', say('designs.insp.dark')],
              ]}
              onChange={(t) => set({ theme: t as CanvasTheme })}
            />
          </Group>
        )}

        <StudioSelect
          label={say('designs.look.font')}
          value={c.font ?? STANDARD_FONT}
          onChange={(value) => onChangeCanvas(withCanvasFont(c, value))}
          options={fontOptions(say('designs.font.standard'), say('designs.font.display'))}
        />

        <Group label={say('designs.insp.background')}>
          {offered.fills && (
            <Seg
              label={say('designs.insp.background')}
              value={fill}
              options={[
                ['colour', say('designs.look.colour')],
                ['gradient', say('designs.look.gradient')],
                ['image', say('designs.look.image')],
              ]}
              onChange={(v) => {
                if (v === 'gradient') set({ backgroundFill: 'gradient', backgroundGradient: c.backgroundGradient ?? GRADIENTS[0] })
                else if (v === 'image') {
                  if (c.backgroundImage?.assetId) set({ backgroundFill: 'image' })
                  else setPicking(true)
                } else set({ backgroundFill: undefined })
              }}
            />
          )}
          {fill === 'colour' && (
            <Swatches
              label=""
              theme={theme}
              value={c.background ?? '--bg-page'}
              none={null}
              paper={paperOnly(doc.kind)}
              choices={
                paperOnly(doc.kind)
                  ? undefined
                  : [
                      { value: '--bg-page', label: say('designs.insp.followsTheme') },
                      { value: '--bg-surface', label: say('designs.insp.surface') },
                      { value: '--bg-muted', label: say('designs.insp.mutedSurface') },
                      { value: '#000000', label: say('designs.insp.black') },
                      { value: '#ffffff', label: say('designs.insp.white') },
                    ]
              }
              onPick={(value) => set({ background: value })}
            />
          )}
          {fill === 'gradient' && (
            <div className="studio-swatches" role="group" aria-label={say('designs.look.gradient')}>
              {GRADIENTS.map((g) => (
                <button
                  key={g}
                  type="button"
                  className="studio-swatch"
                  style={{ background: gradientPaint(g) }}
                  aria-pressed={(c.backgroundGradient ?? GRADIENTS[0]) === g}
                  title={say(GRADIENT_NAME[g])}
                  aria-label={say(GRADIENT_NAME[g])}
                  onClick={() => set({ backgroundGradient: g })}
                />
              ))}
            </div>
          )}
          {fill === 'image' && c.backgroundImage?.assetId && (
            <PictureChoice
              assetId={c.backgroundImage.assetId}
              onChange={() => setPicking(true)}
              onRemove={() => set({ backgroundFill: undefined, backgroundImage: undefined })}
            />
          )}
        </Group>

        {doc.kind === 'counter' && (
          <StudioSelect
            label={say('designs.change.label')}
            value={screenChangeOf(c)}
            onChange={(value) => onChangeCanvas(withScreenChange(c, value as ScreenChange))}
            options={SCREEN_CHANGES.map((m) => ({ value: m.value, label: say(m.label) }))}
          />
        )}

        {offered.ambient && (
          <Toggle
            label={
              <span className="studio-look__toggle" title={say('designs.look.ambientHint')}>
                {say('designs.look.ambient')}
              </span>
            }
            checked={c.ambient === true}
            onChange={(on) => set({ ambient: on || undefined })}
          />
        )}
      </div>

      {children}

      {usageKnown && (
        <section className="studio-look__sec" aria-labelledby="studio-liveon">
          <h3 id="studio-liveon" className="studio-section">
            {say('designs.look.liveOn')}
          </h3>
          {machines.length === 0 && !allBranches && <p className="studio-muted studio-liveon__none">{say('designs.page.notUsed')}</p>}
          {machines.length > 0 && (
            <ul className="studio-liveon">
              {machines.slice(0, LIVE_ROWS).map((m) => {
                const version = m.showing?.layoutId === layoutId ? m.showing.version : undefined
                return (
                  <li key={m.id} title={m.running ? undefined : say('designs.look.offline')}>
                    <Glyph of={m.kind === 'kiosk' ? 'kiosk' : 'display'} size={16} />
                    <span className="studio-liveon__name">
                      {say('designs.look.machine', { kind: say(m.kind === 'kiosk' ? 'designs.page.kind.kiosk' : m.kind === 'desk_display' ? 'designs.page.kind.counter' : 'designs.page.kind.display'), name: m.name })}
                    </span>
                    {version !== undefined && <span className="studio-liveon__v">{say('designs.look.version', { n: version })}</span>}
                  </li>
                )
              })}
            </ul>
          )}
          {machines.length > LIVE_ROWS && <p className="studio-muted">{say('designs.page.more', { n: machines.length - LIVE_ROWS })}</p>}
          {allBranches && <p className="studio-muted">{say('designs.look.allBranches')}</p>}
        </section>
      )}

      {picking && (
        <PicturePicker
          kind={doc.kind}
          onClose={() => setPicking(false)}
          onPick={(asset) => {
            setPicking(false)
            set({ backgroundFill: 'image', backgroundImage: { assetId: asset.id } })
          }}
        />
      )}
    </div>
  )
}

/** A chosen picture, small, with a way to choose another or drop it. */
function PictureChoice({ assetId, onChange, onRemove }: { assetId: string; onChange: () => void; onRemove: () => void }) {
  const { say } = useStaffText()
  return (
    <div className="studio-look__pic">
      <img src={api.assetURL(assetId)} alt="" />
      <Button variant="secondary" size="sm" onPress={onChange}>
        {say('designs.look.change')}
      </Button>
      <Button variant="ghost" size="sm" onPress={onRemove}>
        {say('common.remove')}
      </Button>
    </div>
  )
}
